<script setup lang="ts">
import { elColor } from "@/constants/colors";
import { defineProps, ref } from "vue";
import { Pillar, TwelvePalace } from "@/types/calculators";
import { useI18n } from "vue-i18n";
import ChevronIcon from "@/components/icons/ChevronIcon.vue";

const props = defineProps<{
  pillars: Record<string, Pillar>;
  palaces?: Record<string, TwelvePalace>;
}>();

const { t } = useI18n();

const openAccordions = ref<Record<string, boolean>>({});

function toggle(key: string) {
  openAccordions.value[key] = !openAccordions.value[key] ?? true;
}
</script>

<template>
  <div>
    <div
      class="w-full md:w-[343px] bg-white rounded-xl border border-ternary flex flex-col divide-y divide-ternary overflow-hidden mb-6"
    >
      <div class="grid grid-cols-4 divide-x divide-ternary">
        <div class="h-12 flex items-center justify-center">
          {{ t("calculators.bazi.results.birthday-map.times.hour") }}
        </div>
        <div class="h-12 flex items-center justify-center">
          {{ t("calculators.bazi.results.birthday-map.times.day") }}
        </div>
        <div class="h-12 flex items-center justify-center">
          {{ t("calculators.bazi.results.birthday-map.times.month") }}
        </div>
        <div class="h-12 flex items-center justify-center">
          {{ t("calculators.bazi.results.birthday-map.times.year") }}
        </div>
      </div>

      <div class="grid grid-cols-4 divide-x divide-ternary">
        <div
          :class="elColor[props.pillars.hour.element]"
          class="h-12 flex items-center justify-center"
        >
          {{ props.pillars.hour.stem }}
        </div>
        <div
          :class="elColor[props.pillars.day.element]"
          class="h-12 flex items-center justify-center"
        >
          {{ props.pillars.day.stem }}
        </div>
        <div
          :class="elColor[props.pillars.month.element]"
          class="h-12 flex items-center justify-center"
        >
          {{ props.pillars.month.stem }}
        </div>
        <div
          :class="elColor[props.pillars.year.element]"
          class="h-12 flex items-center justify-center"
        >
          {{ props.pillars.year.stem }}
        </div>
      </div>

      <div class="grid grid-cols-4 divide-x divide-ternary">
        <div class="text-[#dd6c1b] h-12 flex items-center justify-center">
          {{ props.pillars.hour.branch }}
        </div>
        <div class="text-[#dd6c1b] h-12 flex items-center justify-center">
          {{ props.pillars.day.branch }}
        </div>
        <div class="text-[#dd6c1b] h-12 flex items-center justify-center">
          {{ props.pillars.month.branch }}
        </div>
        <div class="text-[#dd6c1b] h-12 flex items-center justify-center">
          {{ props.pillars.year.branch }}
        </div>
      </div>
    </div>
    <div class="grid grid-col-1 gap-4" v-if="palaces">
      <div
        v-for="(palace, key) in palaces"
        :key="key"
        class="bg-white rounded-[20px] min-h-[76px] p-4"
      >
        <button
          @click="toggle(key)"
          class="w-full h-11 flex items-center justify-between"
        >
          <div class="flex items-center gap-4">
            <div
              class="size-11 shrink-0 rounded-xl bg-[#FAEFE7] border border-gold flex items-center justify-center font-semibold text-xl leading-[24px]"
            >
              {{ palace.hieroglyph }}
            </div>
            <span
              class="font-bold text-base leading-[20px] text-black text-left"
            >
              {{ palace.label }}
            </span>
          </div>

          <ChevronIcon :class="{ 'rotate-180': openAccordions[key] }" />
        </button>
        <p
          v-if="openAccordions[key]"
          class="text-base leading-[20px] text-black text-left mt-2"
        >
          {{ palace.description }}
        </p>
      </div>
    </div>
  </div>
</template>
